import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wanted_cart")) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("wanted_cart", JSON.stringify(items));
  }, [items]);

  // Mai oltre la disponibilità reale né oltre il limite per cliente: la quantità in carrello
  // è sempre limitata al minore tra in_stock e max_per_customer
  const capFor = (product) => {
    const stock = product.in_stock ?? 0;
    const maxPer = product.max_per_customer || 0;
    return maxPer > 0 ? Math.min(stock, maxPer) : stock;
  };

  const add = (product, qty = 1) => {
    if (!product.is_consignment) {
      const cap = capFor(product);
      const current = items.find((i) => i.product.id === product.id)?.qty || 0;
      if (current + qty > cap) {
        toast.warning(
          product.max_per_customer && cap === product.max_per_customer && product.max_per_customer < (product.in_stock ?? 0)
            ? `"${product.name}": massimo ${product.max_per_customer} per cliente`
            : cap <= current
              ? `"${product.name}": disponibilità massima già nel carrello (${cap} pz)`
              : `"${product.name}": solo ${cap} pz disponibili`
        );
      }
    }
    setItems((prev) => {
      const ex = prev.find((i) => i.product.id === product.id);
      if (product.is_consignment) return ex ? prev : [...prev, { product, qty: 1 }];
      const cap = capFor(product);
      if (ex) return prev.map((i) => (i.product.id === product.id ? { ...i, qty: Math.min(i.qty + qty, cap) } : i));
      const first = Math.min(qty, cap);
      if (first <= 0) return prev;
      return [...prev, { product, qty: first }];
    });
    setOpen(true);
  };

  const remove = (id) => setItems((prev) => prev.filter((i) => i.product.id !== id));
  const setQty = (id, qty) => {
    const it = items.find((i) => i.product.id === id);
    if (it && !it.product.is_consignment) {
      const cap = capFor(it.product);
      if (qty > cap) {
        toast.warning(it.product.max_per_customer && cap === it.product.max_per_customer && it.product.max_per_customer < (it.product.in_stock ?? 0)
          ? `Massimo ${it.product.max_per_customer} copie per cliente`
          : `Disponibilità massima: ${cap} pz`);
      }
    }
    setItems((prev) =>
      prev.map((i) => {
        if (i.product.id !== id) return i;
        if (i.product.is_consignment) return { ...i, qty: 1 };
        const cap = capFor(i.product) || qty;
        return { ...i, qty: Math.min(qty, cap) };
      }).filter((i) => i.qty > 0)
    );
  };
  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + (i.product.final_price || i.product.price) * i.qty, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, count, subtotal, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
